<template>
  <div class="invoices">
    <div class="main-wrapper">
      <layout-header></layout-header>
      <layout-sidebar></layout-sidebar>

      <div class="page-wrapper">
        <div class="content container-fluid">
          <div class="page-header">
            <div class="row align-items-center">
              <div class="col">
                <h3 class="page-title">
                  {{ pageTitle }}
                  <span
                    class="afu-beta-inline"
                    title="Fitur masih dalam tahap Beta"
                  >BETA</span>
                </h3>
                <p class="text-muted mb-2">
                  Atur alur Follow Up, kontak penerima, pesan, dan jadwal
                  pengiriman.
                </p>
                <ul class="breadcrumb">
                  <li class="breadcrumb-item">
                    <router-link to="/index">Dashboard</router-link>
                  </li>
                  <li class="breadcrumb-item">
                    <router-link to="/auto-follow-up">
                      Auto Follow Up
                    </router-link>
                  </li>
                  <li class="breadcrumb-item active">{{ pageTitle }}</li>
                </ul>
              </div>
              <div class="col-auto float-right ml-auto afu-header-actions">
                <span
                  v-if="isEdit"
                  class="afu-status-badge mr-2"
                  :class="'afu-status-' + statusTone(form.status)"
                >
                  {{ statusLabel(form.status) }}
                </span>
                <button
                  type="button"
                  class="btn btn-outline-secondary"
                  :disabled="isBusy"
                  @click="goBack"
                >
                  <i class="fa fa-arrow-left"></i>
                  Kembali
                </button>
              </div>
            </div>
          </div>

          <div v-if="pageLoading" class="card afu-form-card">
            <div class="card-body text-center py-5">
              <i class="fa fa-spinner fa-spin fa-2x text-muted"></i>
              <p class="text-muted mt-3 mb-0">Memuat alur...</p>
            </div>
          </div>

          <div v-else-if="loadError" class="card afu-form-card">
            <div class="card-body py-5 text-center">
              <i class="fa fa-exclamation-triangle fa-2x text-danger"></i>
              <h5 class="mt-3">Alur belum dapat dimuat</h5>
              <p class="text-muted">{{ loadError }}</p>
              <button
                type="button"
                class="btn btn-outline-primary mr-2"
                @click="retryLoad"
              >
                Coba Lagi
              </button>
              <button
                type="button"
                class="btn btn-outline-secondary"
                @click="goToList"
              >
                Kembali ke Daftar
              </button>
            </div>
          </div>

          <template v-else>
            <div class="afu-wizard-body">
              <div v-if="isReadOnly" class="alert alert-secondary">
                <i class="fa fa-lock"></i>
                Alur ini tidak bisa diubah karena sudah
                <strong>{{ statusLabel(form.status) }}</strong>. Anda hanya bisa
                melihat pengaturannya.
              </div>

              <div v-else-if="isStructureLocked" class="alert alert-info">
                <i class="fa fa-lock"></i>
                Alur ini sedang <strong>{{ statusLabel(form.status) }}</strong>.
                Anda masih bisa mengubah nama, deskripsi, jeda, jam kirim, dan
                pilihan berhenti saat pelanggan membalas. Batas kontak lisensi
                diubah melalui tindakan terpisah. Pesan, sumber kontak, dan
                nomor pengirim dikunci agar jadwal dan daftar kontak yang sudah
                berjalan tidak berubah.
                <div class="mt-2">
                  Perubahan pesan dan sumber kontak hanya bisa dilakukan saat
                  alur masih berstatus <strong>Draf</strong>.
                </div>
              </div>

              <div v-if="autoSwitchEnabled" class="alert alert-warning">
                <i class="fa fa-exchange"></i>
                <strong>Pengalihan nomor pengirim aktif.</strong>
                Waajo dapat mengirim pesan dengan nomor lain yang sedang
                terhubung. Nomor yang Anda pilih tetap menjadi acuan kuota Auto
                Follow Up, tetapi nomor pengirim sebenarnya bisa berbeda.
              </div>

              <div v-if="activationError" class="alert alert-danger">
                <i class="fa fa-exclamation-circle"></i>
                {{ activationError }}
              </div>

              <div v-if="serverError" class="alert alert-danger">
                <i class="fa fa-exclamation-circle"></i>
                {{ serverError }}
              </div>

              <div v-if="validationSummary" class="alert alert-danger">
                <i class="fa fa-exclamation-circle"></i>
                {{ validationSummary }}
              </div>

              <div class="afu-section-nav">
                <a
                  href="#afu-basic"
                  @click.prevent="scrollToSection('afu-basic')"
                >
                  1. Informasi Dasar
                </a>
                <a
                  href="#afu-sources"
                  @click.prevent="scrollToSection('afu-sources')"
                >
                  2. Sumber Kontak
                </a>
                <a
                  href="#afu-steps"
                  @click.prevent="scrollToSection('afu-steps')"
                >
                  3. Rangkaian Pesan
                </a>
                <a
                  href="#afu-settings"
                  @click.prevent="scrollToSection('afu-settings')"
                >
                  4. Pengaturan
                </a>
              </div>

              <div id="afu-basic" class="card afu-form-card">
                <div class="card-header afu-card-header">
                  <span class="afu-section-number">1</span>
                  <div>
                    <h5>Informasi Dasar</h5>
                    <small class="text-muted">
                      Nama alur dan nomor pengirim yang digunakan.
                    </small>
                  </div>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-8">
                      <div class="form-group">
                        <label>
                          Nama alur
                          <span class="text-danger">*</span>
                        </label>
                        <input
                          v-model="form.name"
                          type="text"
                          class="form-control"
                          :class="{ 'is-invalid': !!fieldError('name') }"
                          maxlength="255"
                          placeholder="Contoh: Follow-up Calon Pelanggan"
                          :disabled="isReadOnly"
                        />
                        <div v-if="fieldError('name')" class="afu-field-error">
                          {{ fieldError("name") }}
                        </div>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-group">
                        <label>Status alur</label>
                        <div class="afu-status-line">
                          <span
                            class="afu-status-badge"
                            :class="'afu-status-' + statusTone(form.status)"
                          >
                            {{ statusLabel(form.status) }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="form-group">
                    <label>Deskripsi</label>
                    <textarea
                      v-model="form.description"
                      class="form-control"
                      rows="3"
                      placeholder="Deskripsi opsional untuk alur ini"
                      :disabled="isReadOnly"
                    ></textarea>
                    <small class="text-muted">
                      Deskripsi membantu membedakan alur satu dengan lainnya.
                    </small>
                  </div>

                  <div class="row">
                    <div class="col-md-7">
                      <div class="form-group">
                        <label>
                          Nomor WhatsApp pengirim
                          <span class="text-danger">*</span>
                        </label>
                        <a-select
                          v-model="selectedLicenseId"
                          show-search
                          placeholder="Cari nama atau nomor WhatsApp"
                          style="width: 100%"
                          :filter-option="false"
                          :disabled="isReadOnly || isStructureLocked"
                          :loading="licenseLoading"
                          @search="searchLicenses"
                          @focus="loadLicenses('')"
                          @change="onLicenseChange"
                        >
                          <a-select-option
                            v-for="license in licenseSelectOptions"
                            :key="license.id"
                            :value="license.id"
                          >
                            <span>
                              {{ license.license_name || license.license_phone_number || "Nomor WhatsApp" }}
                            </span>
                            <span class="text-muted">
                              — {{ license.license_phone_number || "-" }}
                            </span>
                            <span
                              v-if="license.status_label"
                              class="afu-license-option-status"
                            >
                              {{ licenseStatusLabel(license) }}
                            </span>
                            <i
                              v-if="license.is_online_status == 1"
                              class="fa fa-signal text-success ml-1"
                              title="Online"
                            ></i>
                          </a-select-option>
                        </a-select>
                        <div
                          v-if="licenseError"
                          class="afu-inline-error d-flex align-items-center justify-content-between"
                        >
                          <span>{{ licenseError }}</span>
                          <button
                            type="button"
                            class="btn btn-sm btn-outline-secondary ml-2"
                            @click="loadLicenses('')"
                          >
                            Coba Lagi
                          </button>
                        </div>
                        <div
                          v-if="fieldError('license_id')"
                          class="afu-field-error"
                        >
                          {{ fieldError("license_id") }}
                        </div>
                        <small v-if="selectedLicense" class="text-muted">
                          Dipilih:
                          {{ licenseLabel(selectedLicense) }}
                          <span v-if="licenseStatusLabel(selectedLicense)">
                            ({{ licenseStatusLabel(selectedLicense) }})
                          </span>
                        </small>
                        <div
                          v-if="selectedLicenseWarning"
                          class="afu-inline-warning mt-2"
                        >
                          {{ selectedLicenseWarning }}
                        </div>
                      </div>
                    </div>
                    <div class="col-md-5">
                      <div class="afu-info-box">
                        <div class="afu-info-box-title">
                          <i class="fa fa-info-circle"></i>
                          Nomor pengirim dan kuota
                        </div>
                        <p class="mb-2">
                          Nomor yang dipilih menjadi acuan kuota harian Auto
                          Follow Up.
                        </p>
                        <p class="mb-0" v-if="autoSwitchEnabled">
                          Jika pengalihan nomor pengirim aktif, pesan bisa
                          dikirim dari nomor lain yang sedang terhubung. Kuota
                          tetap mengikuti nomor yang dipilih.
                        </p>
                        <p class="mb-0" v-else>
                          Pesan akan dikirim melalui nomor WhatsApp yang dipilih.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div id="afu-sources" class="card afu-form-card">
                <div class="card-header afu-card-header">
                  <span class="afu-section-number">2</span>
                  <div>
                    <h5>Sumber Kontak</h5>
                    <small class="text-muted">
                      Pilih grup, tag, atau kontak manual.
                    </small>
                  </div>
                  <span class="afu-section-count">
                    {{ allSelectedSources.length }} sumber
                  </span>
                </div>
                <div class="card-body">
                  <template v-if="isStructureLocked || isReadOnly">
                    <div class="afu-readonly-box">
                      <div v-if="!allSelectedSources.length" class="text-muted">
                        Belum ada sumber kontak tersimpan.
                      </div>
                      <span
                        v-for="source in allSelectedSources"
                        :key="
                          source.source_type + ':' + source.source_id
                        "
                        class="afu-source-badge"
                      >
                        {{ sourceTypeLabel(source.source_type) }}:
                        {{ sourceDisplay(source) }}
                      </span>
                    </div>
                    <p class="text-muted small mb-0 mt-2">
                      Sumber kontak dikunci karena alur sedang berjalan, dijeda,
                      atau sudah tidak bisa diubah.
                    </p>
                  </template>

                  <template v-else>
                    <div class="afu-source-tabs">
                      <a-radio-group v-model="sourceMode" button-style="solid">
                        <a-radio-button value="group">
                          <i class="fa fa-users"></i>
                          Grup Kontak
                        </a-radio-button>
                        <a-radio-button value="tag">
                          <i class="fa fa-tags"></i>
                          Tag
                        </a-radio-button>
                        <a-radio-button value="manual">
                          <i class="fa fa-user"></i>
                          Kontak Manual
                        </a-radio-button>
                      </a-radio-group>
                    </div>

                    <div v-if="sourceMode === 'group'" class="afu-source-panel">
                      <label>Pilih grup kontak</label>
                      <multiselect
                        v-model="selectedGroups"
                        :options="groupOptions"
                        label="name"
                        track-by="id"
                        placeholder="Cari grup kontak..."
                        :multiple="true"
                        :searchable="true"
                        :loading="groupLoading"
                        :internal-search="false"
                        :clear-on-select="false"
                        :close-on-select="false"
                        :options-limit="300"
                        :limit="4"
                        :max-height="420"
                        :show-no-results="true"
                        :hide-selected="true"
                        @open="loadGroups('')"
                        @search-change="loadGroups"
                      >
                        <span slot="noResult">Grup tidak ditemukan.</span>
                      </multiselect>
                      <div v-if="groupError" class="afu-inline-error">
                        {{ groupError }}
                      </div>
                      <small class="text-muted">
                        Jika memilih beberapa grup, kontak dari semua grup
                        digabung. Nomor yang sama hanya dihitung sekali saat alur
                        diaktifkan.
                      </small>
                    </div>

                    <div v-if="sourceMode === 'tag'" class="afu-source-panel">
                      <label>Pilih tag</label>
                      <multiselect
                        v-model="selectedTags"
                        :options="tagOptions"
                        label="name"
                        track-by="id"
                        placeholder="Cari tag..."
                        :multiple="true"
                        :searchable="true"
                        :loading="tagLoading"
                        :internal-search="false"
                        :clear-on-select="false"
                        :close-on-select="false"
                        :options-limit="300"
                        :limit="4"
                        :max-height="420"
                        :show-no-results="true"
                        :hide-selected="true"
                        @open="loadTags('')"
                        @search-change="loadTags"
                      >
                        <span slot="noResult">Tag tidak ditemukan.</span>
                      </multiselect>
                      <div v-if="tagError" class="afu-inline-error">
                        {{ tagError }}
                      </div>
                      <small class="text-muted">
                        Kontak yang memiliki salah satu tag akan diikutkan.
                        Nomor yang sama hanya dihitung sekali saat alur
                        diaktifkan.
                      </small>
                    </div>

                    <div
                      v-if="sourceMode === 'manual'"
                      class="afu-source-panel"
                    >
                      <label>Pilih kontak satu per satu</label>
                      <multiselect
                        v-model="selectedContacts"
                        :options="contactOptions"
                        label="first_name"
                        track-by="id"
                        placeholder="Cari nama atau nomor kontak..."
                        :custom-label="contactLabel"
                        :multiple="true"
                        :searchable="true"
                        :loading="contactLoading"
                        :internal-search="false"
                        :clear-on-select="false"
                        :close-on-select="false"
                        :options-limit="300"
                        :limit="4"
                        :max-height="420"
                        :show-no-results="true"
                        :hide-selected="true"
                        @open="loadContacts('')"
                        @search-change="loadContacts"
                      >
                        <span slot="noResult">
                          Kontak personal tidak ditemukan.
                        </span>
                      </multiselect>
                      <div v-if="contactError" class="afu-inline-error">
                        {{ contactError }}
                      </div>
                      <small class="text-muted">
                        Hanya kontak pribadi yang memiliki nomor WhatsApp.
                        Kontak grup tidak bisa dipilih di sini.
                      </small>
                    </div>

                    <div class="afu-source-summary">
                      <div class="afu-source-summary-item">
                        <span class="afu-source-summary-value">
                          {{ selectedGroups.length }}
                        </span>
                        <span>grup</span>
                      </div>
                      <div class="afu-source-summary-item">
                        <span class="afu-source-summary-value">
                          {{ selectedTags.length }}
                        </span>
                        <span>tag</span>
                      </div>
                      <div class="afu-source-summary-item">
                        <span class="afu-source-summary-value">
                          {{ selectedContacts.length }}
                        </span>
                        <span>kontak manual</span>
                      </div>
                      <div class="afu-source-summary-item afu-source-summary-wide">
                        <span class="afu-source-summary-value">
                          {{ contactEstimateLabel }}
                        </span>
                        <span>perkiraan jumlah kontak</span>
                      </div>
                    </div>

                    <p class="text-muted small mb-0 mt-2">
                      Semua sumber yang dipilih digabung. Nomor yang sama hanya
                      dihitung sekali. Angka di atas masih perkiraan; jumlah
                      kontak akhir ditentukan saat alur diaktifkan.
                    </p>
                    <div
                      v-if="fieldError('sources')"
                      class="afu-field-error mt-2"
                    >
                      {{ fieldError("sources") }}
                    </div>
                  </template>
                </div>
              </div>

              <div id="afu-steps" class="card afu-form-card">
                <div class="card-header afu-card-header">
                  <span class="afu-section-number">3</span>
                  <div>
                    <h5>Rangkaian Pesan</h5>
                    <small class="text-muted">
                      Setiap kartu adalah satu pesan yang dijadwalkan.
                    </small>
                  </div>
                  <span class="afu-section-count">
                    {{ form.steps.length }} pesan
                  </span>
                </div>
                <div class="card-body">
                  <div class="row afu-steps-layout">
                    <div class="col-xl-7 afu-steps-editor-col">
                      <div class="afu-schedule-note">
                        <i class="fa fa-clock-o"></i>
                        Pesan pertama dihitung dari waktu kontak masuk. Pesan
                        berikutnya dihitung dari pesan sebelumnya yang berhasil
                        dikirim. Jadwal aktual dapat bergeser ke jam kirim
                        berikutnya.
                      </div>

                      <div
                        v-for="(step, index) in form.steps"
                        :key="step._key"
                        class="afu-step-card"
                        :class="{ 'is-preview-active': previewStepIndex === index }"
                        @click="selectPreviewStep(index)"
                        @focusin="selectPreviewStep(index)"
                      >
                        <div class="afu-step-header">
                          <div class="afu-step-heading">
                            <span class="afu-step-number">
                              Pesan {{ index + 1 }}
                            </span>
                            <span class="text-muted small">
                              {{ stepDelayLabel(index) }}
                            </span>
                          </div>
                          <div class="afu-step-actions">
                            <button
                              type="button"
                              class="btn btn-sm btn-outline-secondary afu-preview-step-btn"
                              :class="{ 'is-active': previewStepIndex === index }"
                              title="Tampilkan pesan ini di preview WhatsApp"
                              @click="selectPreviewStep(index, true)"
                            >
                              <i class="fa fa-eye"></i>
                              Preview
                            </button>
                            <button
                              v-if="
                                !isStructureLocked &&
                                !isReadOnly &&
                                form.steps.length > 1
                              "
                              type="button"
                              class="btn btn-sm btn-outline-danger ml-1"
                              title="Hapus pesan"
                              @click="removeStep(index)"
                            >
                              <i class="fa fa-trash"></i>
                              Hapus
                            </button>
                          </div>
                        </div>

                        <template v-if="isStructureLocked || isReadOnly">
                          <div class="afu-step-readonly">
                            <div class="afu-step-readonly-top">
                              <strong>
                                {{ step.name || "Pesan " + (index + 1) }}
                              </strong>
                              <span class="badge badge-light">
                                {{ messageTypeLabel(step.message_type) }}
                              </span>
                            </div>
                            <div class="text-muted small mb-2">
                              Jeda {{ step.delay_value }}
                              {{ delayUnitLabel(step.delay_unit) }}
                            </div>
                            <div
                              v-if="step.message_content"
                              class="afu-step-content"
                            >
                              {{ step.message_content }}
                            </div>
                            <div
                              v-else-if="step.message_type !== 'text'"
                              class="text-muted small"
                            >
                              Tanpa teks pendamping.
                            </div>
                            <a
                              v-if="step.message_media"
                              :href="step.message_media"
                              target="_blank"
                              rel="noopener noreferrer"
                              class="d-inline-block mt-2"
                            >
                              <i class="fa fa-paperclip"></i>
                              Lihat media
                            </a>
                          </div>
                        </template>

                        <template v-else>
                          <div class="row">
                            <div class="col-md-5">
                              <div class="form-group">
                                <label>Nama pesan</label>
                                <input
                                  v-model="step.name"
                                  type="text"
                                  class="form-control"
                                  maxlength="255"
                                  placeholder="Contoh: Sapaan awal"
                                />
                              </div>
                            </div>
                            <div class="col-md-3">
                              <div class="form-group">
                                <label>{{ stepDelayLabel(index) }}</label>
                                <div class="afu-delay-row">
                                  <a-input-number
                                    v-model="step.delay_value"
                                    :min="0"
                                    :precision="0"
                                    style="width: 100%"
                                  />
                                  <select
                                    v-model="step.delay_unit"
                                    class="form-control afu-delay-unit"
                                  >
                                    <option value="minute">menit</option>
                                    <option value="hour">jam</option>
                                    <option value="day">hari</option>
                                  </select>
                                </div>
                                <div
                                  v-if="stepFieldError(step, 'delay')"
                                  class="afu-field-error"
                                >
                                  {{ stepFieldError(step, "delay") }}
                                </div>
                              </div>
                            </div>
                            <div class="col-md-4">
                              <div class="form-group">
                                <label>Jenis pesan</label>
                                <a-radio-group
                                  v-model="step.message_type"
                                  button-style="solid"
                                  size="small"
                                  :disabled="step.uploading"
                                  @change="handleStepTypeChange(step)"
                                >
                                  <a-radio-button value="text">
                                    Teks
                                  </a-radio-button>
                                  <a-radio-button value="image">
                                    Gambar
                                  </a-radio-button>
                                  <a-radio-button value="video">
                                    Video
                                  </a-radio-button>
                                  <a-radio-button value="document">
                                    Dokumen
                                  </a-radio-button>
                                </a-radio-group>
                              </div>
                            </div>
                          </div>

                          <div class="form-group">
                            <label>
                              {{
                                step.message_type === "text"
                                  ? "Isi pesan"
                                  : "Teks pendamping (opsional)"
                              }}
                              <span
                                v-if="step.message_type === 'text'"
                                class="text-danger"
                              >
                                *
                              </span>
                            </label>
                            <chat-component v-model="step.message_content" />
                            <div
                              v-if="stepFieldError(step, 'content')"
                              class="afu-field-error afu-step-error"
                            >
                              {{ stepFieldError(step, "content") }}
                            </div>
                            <small class="text-muted">
                              Gunakan tombol Variabel untuk menyisipkan data seperti
                              nama atau nomor kontak.
                            </small>
                          </div>

                          <div
                            v-if="step.message_type !== 'text'"
                            class="afu-media-section"
                          >
                            <div
                              class="d-flex align-items-center justify-content-between flex-wrap mb-2"
                            >
                              <label class="mb-0">
                                Media {{ mediaTypeLabel(step.message_type) }}
                                <span class="text-danger">*</span>
                              </label>
                              <a-checkbox
                                :checked="!!step.is_custom_url"
                                @change="
                                  event =>
                                    onCustomUrlToggle(step, event.target.checked)
                                "
                              >
                                Gunakan tautan file
                              </a-checkbox>
                            </div>

                            <div v-if="step.is_custom_url">
                              <input
                                v-model="step.message_media"
                                type="text"
                                class="form-control"
                                placeholder="https://contoh.com/file.jpg"
                                @input="step.uploadError = ''"
                              />
                              <small class="text-muted">
                                Masukkan tautan file yang dapat diakses, diawali
                                http:// atau https://.
                              </small>
                            </div>

                            <div v-else>
                              <div class="d-flex align-items-center flex-wrap">
                                <a-upload
                                  name="file"
                                  :action="uploadAction"
                                  :headers="uploadHeaders"
                                  :data="{ message_type: step.message_type }"
                                  :show-upload-list="false"
                                  :accept="mediaAccept(step)"
                                  :before-upload="
                                    file => beforeMediaUpload(step, file)
                                  "
                                  @change="info => handleMediaUpload(step, info)"
                                >
                                  <button
                                    type="button"
                                    class="btn btn-outline-secondary"
                                    :disabled="step.uploading"
                                  >
                                    <i
                                      :class="
                                        step.uploading
                                          ? 'fa fa-spinner fa-spin'
                                          : 'fa fa-cloud-upload'
                                      "
                                    ></i>
                                    {{
                                      step.message_media
                                        ? "Ganti file"
                                        : "Pilih file"
                                    }}
                                  </button>
                                </a-upload>
                                <span
                                  v-if="step.uploading"
                                  class="text-muted small ml-2"
                                >
                                  Mengunggah...
                                </span>
                              </div>

                              <div
                                v-if="step.message_media"
                                class="afu-media-existing"
                              >
                                <i class="fa fa-paperclip"></i>
                                <a
                                  :href="step.message_media"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {{
                                    step.media_name ||
                                    fileNameFromUrl(step.message_media)
                                  }}
                                </a>
                                <button
                                  type="button"
                                  class="btn btn-sm btn-link text-danger"
                                  @click="clearStepMedia(step)"
                                >
                                  Hapus
                                </button>
                              </div>

                              <small class="text-muted">
                                {{ mediaHint(step.message_type) }}
                              </small>
                            </div>

                            <div
                              v-if="step.uploadError"
                              class="afu-field-error"
                            >
                              {{ step.uploadError }}
                            </div>
                            <div
                              v-if="stepFieldError(step, 'media')"
                              class="afu-field-error"
                            >
                              {{ stepFieldError(step, "media") }}
                            </div>
                          </div>
                        </template>
                      </div>

                      <button
                        v-if="!isStructureLocked && !isReadOnly"
                        type="button"
                        class="btn btn-outline-primary afu-add-step-btn"
                        @click="addStep"
                      >
                        <i class="fa fa-plus"></i>
                        Tambah Pesan
                      </button>

                      <div
                        v-if="fieldError('steps')"
                        class="afu-field-error mt-2"
                      >
                        {{ fieldError("steps") }}
                      </div>
                    </div>
                    <div class="col-xl-5 afu-steps-preview-col">
                      <afu-whatsapp-preview
                        :steps="form.steps"
                        :active-index.sync="previewStepIndex"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div id="afu-settings" class="card afu-form-card">
                <div class="card-header afu-card-header">
                  <span class="afu-section-number">4</span>
                  <div>
                    <h5>Pengaturan Pengiriman</h5>
                    <small class="text-muted">
                      Batasi volume dan tentukan jam kirim.
                    </small>
                  </div>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-3 col-sm-6">
                      <div class="form-group">
                        <label>Kontak minimal per pengiriman</label>
                        <a-input-number
                          v-model="form.batch_min_contacts"
                          :min="1"
                          :precision="0"
                          style="width: 100%"
                          :disabled="isReadOnly"
                        />
                        <small class="text-muted">kontak per kelompok pengiriman</small>
                        <div
                          v-if="fieldError('batch_min_contacts')"
                          class="afu-field-error"
                        >
                          {{ fieldError("batch_min_contacts") }}
                        </div>
                      </div>
                    </div>
                    <div class="col-md-3 col-sm-6">
                      <div class="form-group">
                        <label>Kontak maksimal per pengiriman</label>
                        <a-input-number
                          v-model="form.batch_max_contacts"
                          :min="1"
                          :precision="0"
                          style="width: 100%"
                          :disabled="isReadOnly"
                        />
                        <small class="text-muted">kontak per kelompok pengiriman</small>
                        <div
                          v-if="fieldError('batch_max_contacts')"
                          class="afu-field-error"
                        >
                          {{ fieldError("batch_max_contacts") }}
                        </div>
                      </div>
                    </div>
                    <div class="col-md-3 col-sm-6">
                      <div class="form-group">
                        <label>Jeda minimal</label>
                        <a-input-number
                          v-model="delayMinMinutes"
                          :min="0.5"
                          :step="0.5"
                          :precision="1"
                          style="width: 100%"
                          :disabled="isReadOnly"
                        />
                        <small class="text-muted">menit antar-pesan</small>
                        <div
                          v-if="fieldError('delay_min')"
                          class="afu-field-error"
                        >
                          {{ fieldError("delay_min") }}
                        </div>
                      </div>
                    </div>
                    <div class="col-md-3 col-sm-6">
                      <div class="form-group">
                        <label>Jeda maksimal</label>
                        <a-input-number
                          v-model="delayMaxMinutes"
                          :min="0.5"
                          :step="0.5"
                          :precision="1"
                          style="width: 100%"
                          :disabled="isReadOnly"
                        />
                        <small class="text-muted">menit antar-pesan</small>
                        <div
                          v-if="fieldError('delay_max')"
                          class="afu-field-error"
                        >
                          {{ fieldError("delay_max") }}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="row">
                    <div class="col-md-4">
                      <div class="form-group">
                        <label>Batas kontak lisensi per hari</label>
                        <div class="afu-license-quota-readonly">
                          <template v-if="licenseQuotaLoading">
                            <i class="fa fa-spinner fa-spin"></i>
                            Memuat batas lisensi...
                          </template>
                          <template v-else-if="licenseQuota">
                            <strong>{{ licenseQuota.daily_limit || 0 }} kontak/hari</strong>
                            <button
                              type="button"
                              class="btn btn-sm btn-outline-secondary ml-2"
                              :disabled="quotaSaving"
                              @click="openQuotaSettings"
                            >
                              Atur batas lisensi
                            </button>
                          </template>
                          <template v-else>
                            <span class="text-muted">Pilih nomor pengirim terlebih dahulu.</span>
                          </template>
                        </div>
                        <small class="text-muted d-block mt-1">
                          Semua alur dengan nomor pengirim ini berbagi kuota lisensi.
                          Batas tidak disimpan per alur.
                        </small>
                        <div
                          v-if="licenseQuotaError"
                          class="afu-inline-error mt-1"
                        >
                          {{ licenseQuotaError }}
                        </div>
                      </div>
                    </div>
                    <div class="col-md-4 col-sm-6">
                      <div class="form-group">
                        <label>Jam kirim mulai</label>
                        <a-time-picker
                          v-model="form.sending_start_time"
                          format="HH:mm"
                          valueFormat="HH:mm"
                          placeholder="08:00"
                          style="width: 100%"
                          :disabled="isReadOnly"
                        />
                        <div
                          v-if="fieldError('sending_start_time')"
                          class="afu-field-error"
                        >
                          {{ fieldError("sending_start_time") }}
                        </div>
                      </div>
                    </div>
                    <div class="col-md-4 col-sm-6">
                      <div class="form-group">
                        <label>Jam kirim selesai</label>
                        <a-time-picker
                          v-model="form.sending_end_time"
                          format="HH:mm"
                          valueFormat="HH:mm"
                          placeholder="20:00"
                          style="width: 100%"
                          :disabled="isReadOnly"
                        />
                        <div
                          v-if="fieldError('sending_end_time')"
                          class="afu-field-error"
                        >
                          {{ fieldError("sending_end_time") }}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    v-if="delayWindowCrossesMidnight"
                    class="afu-inline-info mb-3"
                  >
                    <i class="fa fa-moon-o"></i>
                    Jam kirim melewati tengah malam. Pengiriman tetap mengikuti jam yang Anda atur.
                  </div>
                  <div
                    v-if="sendingWindowAlwaysOpen"
                    class="afu-inline-info mb-3"
                  >
                    <i class="fa fa-clock-o"></i>
                    Jam mulai dan selesai sama, sehingga pengiriman dapat berjalan sepanjang hari.
                  </div>

                  <div class="afu-switch-row">
                    <div>
                      <strong>Hentikan saat pelanggan membalas</strong>
                      <div class="text-muted small">
                        Pesan yang masih menunggu tidak akan dikirim setelah
                        pelanggan membalas.
                      </div>
                    </div>
                    <a-switch
                      :checked="Number(form.stop_on_customer_reply) === 1"
                      :disabled="isReadOnly"
                      @change="onStopOnReplyChange"
                    />
                  </div>

                  <div class="alert alert-warning mb-0 mt-4">
                    <i class="fa fa-shield"></i>
                    Pengaturan jumlah dan jeda membantu menjaga pengiriman
                    tetap wajar, tetapi tidak menjamin nomor terhindar dari
                    pembatasan WhatsApp. Gunakan batas dan jeda yang wajar.
                  </div>

                  <p class="text-muted small mb-0 mt-3">
                    Perubahan pengaturan pada alur yang sedang berjalan tidak
                    menjadwalkan ulang atau membatalkan pesan yang sudah ada.
                  </p>
                </div>
              </div>

              <div class="card afu-form-actions">
                <div class="afu-form-actions-left">
                  <button
                    type="button"
                    class="btn btn-outline-secondary"
                    :disabled="isBusy"
                    @click="goBack"
                  >
                    <i class="fa fa-arrow-left"></i>
                    Kembali ke Daftar
                  </button>
                </div>
                <div class="afu-form-actions-right">
                  <button
                    v-if="canSaveDraft"
                    type="button"
                    class="btn btn-outline-primary"
                    :disabled="isBusy || isUploading"
                    @click="save('draft')"
                  >
                    <i
                      :class="
                        isSaving && saveIntent === 'draft'
                          ? 'fa fa-spinner fa-spin'
                          : 'fa fa-save'
                      "
                    ></i>
                    Simpan Draf
                  </button>
                  <button
                    v-if="canSaveChanges"
                    type="button"
                    class="btn afu-primary-btn"
                    :disabled="isBusy || isUploading"
                    @click="save('changes')"
                  >
                    <i
                      :class="
                        isSaving
                          ? 'fa fa-spinner fa-spin'
                          : 'fa fa-check'
                      "
                    ></i>
                    Simpan Perubahan
                  </button>
                  <button
                    v-if="canActivate"
                    type="button"
                    class="btn afu-primary-btn"
                    :disabled="isBusy || isUploading"
                    @click="save('activate')"
                  >
                    <i
                      :class="
                        isSaving || isActivating
                          ? 'fa fa-spinner fa-spin'
                          : 'fa fa-play'
                      "
                    ></i>
                    Aktifkan
                  </button>
                </div>
              </div>

              <p v-if="canActivate" class="text-muted small mb-0">
                Tombol Aktifkan menyimpan draf terlebih dahulu, lalu
                mengaktifkan alur. Waajo akan menentukan daftar kontak dan
                jadwal saat aktivasi.
              </p>
            </div>
          </template>
        </div>
      </div>
    </div>

  <a-modal
    :visible="quotaModalVisible"
    title="Atur Batas Kuota Lisensi"
    :footer="null"
    :closable="!quotaSaving"
    wrap-class-name="afu-history-modal"
    @cancel="closeQuotaSettings"
  >
    <p class="text-muted">
      Perubahan ini berlaku untuk semua Auto Follow Up yang memakai nomor
      pengirim yang sama. Histori pengiriman tidak dihapus.
    </p>
    <div v-if="licenseQuota" class="mb-3">
      <div class="afu-quota-detail-row"><span>Lisensi</span><strong>{{ licenseQuota.license_phone_number || licenseQuota.license_id }}</strong></div>
      <div class="afu-quota-detail-row"><span>Batas saat ini</span><strong>{{ licenseQuota.daily_limit }} kontak/hari</strong></div>
      <div class="afu-quota-detail-row"><span>Sudah digunakan</span><strong>{{ licenseQuota.sent_count || 0 }} kontak</strong></div>
      <div class="afu-quota-detail-row"><span>Reservasi aktif</span><strong>{{ licenseQuota.reserved_count || 0 }} kontak</strong></div>
      <div class="afu-quota-detail-row"><span>Sisa</span><strong>{{ licenseQuota.remaining_count || 0 }} kontak</strong></div>
    </div>
    <div class="form-group">
      <label>Batas kontak baru per hari <span class="text-danger">*</span></label>
      <a-input-number
        v-model="quotaNewLimit"
        :min="1"
        :precision="0"
        style="width: 100%"
        :disabled="quotaSaving"
      />
    </div>
    <div class="form-group">
      <label>Alasan perubahan <span class="text-danger">*</span></label>
      <textarea
        v-model="quotaReason"
        class="form-control"
        rows="3"
        maxlength="500"
        :disabled="quotaSaving"
        placeholder="Contoh: penyesuaian batas operasional lisensi"
      ></textarea>
    </div>
    <div v-if="quotaError" class="afu-inline-error mb-3">{{ quotaError }}</div>
    <div class="text-right">
      <button type="button" class="btn btn-light" :disabled="quotaSaving" @click="closeQuotaSettings">Batal</button>
      <button type="button" class="btn btn-primary ml-2" :disabled="quotaSaving" @click="saveQuotaSettings">
        <i class="fa" :class="quotaSaving ? 'fa-spinner fa-spin' : 'fa-check'"></i>
        Simpan Batas Lisensi
      </button>
    </div>
  </a-modal>
  </div>
</template>

<script>
import LayoutHeader from "@/components/layouts/Header.vue";
import LayoutSidebar from "@/components/layouts/Sidebar.vue";
import Multiselect from "vue-multiselect";
require("vue-multiselect/dist/vue-multiselect.min.css");
import ChatComponent from "@/views/broadcast/componentsV2/Forms/chatComponent.vue";
import AfuWhatsAppPreview from "./components/AfuWhatsAppPreview.vue";
import { APICore } from "@/utils/servicesApi";
import { getXAuthToken } from "@/utils/auth";

const DEFAULT_DAILY_LIMIT = 100;
const DEFAULT_BATCH_MIN = 15;
const DEFAULT_BATCH_MAX = 20;
const DEFAULT_DELAY_MIN_SECONDS = 60;
const DEFAULT_DELAY_MAX_SECONDS = 600;

const STATUS_LABELS = {
  draft: "Draf",
  active: "Aktif",
  paused: "Dijeda",
  completed: "Selesai",
  cancelled: "Dibatalkan"
};

const SOURCE_TYPE_LABELS = {
  group: "Grup",
  tag: "Tag",
  manual: "Kontak"
};

const MESSAGE_TYPE_LABELS = {
  text: "Teks",
  image: "Gambar",
  video: "Video",
  document: "Dokumen"
};

const DELAY_UNIT_LABELS = {
  minute: "menit",
  hour: "jam",
  day: "hari"
};

const DELAY_UNITS = ["minute", "hour", "day"];
const MESSAGE_TYPES = ["text", "image", "video", "document"];

function randomKey(prefix) {
  return (
    prefix +
    "-" +
    Date.now().toString(36) +
    "-" +
    Math.random().toString(36).slice(2, 8)
  );
}

function defaultStep(index) {
  return {
    _key: randomKey("step"),
    id: "",
    step_order: index + 1,
    name: "Pesan " + (index + 1),
    delay_value: index === 0 ? 2 : 1,
    delay_unit: index === 0 ? "hour" : "day",
    message_type: "text",
    message_content: "",
    message_media: "",
    is_custom_url: false,
    media_name: "",
    uploading: false,
    uploadError: ""
  };
}

function defaultForm() {
  return {
    id: "",
    status: "draft",
    name: "",
    description: "",
    license_id: "",
    stop_on_customer_reply: 1,
    daily_contact_limit: DEFAULT_DAILY_LIMIT,
    batch_min_contacts: DEFAULT_BATCH_MIN,
    batch_max_contacts: DEFAULT_BATCH_MAX,
    sending_start_time: "08:00",
    sending_end_time: "20:00",
    steps: [defaultStep(0)]
  };
}

function normalizeQueryId(value) {
  if (Array.isArray(value)) {
    return value[0] || "";
  }
  return value || "";
}

function positiveNumber(value, fallback) {
  const number = Number(value);
  if (isFinite(number) && number > 0) {
    return number;
  }
  return fallback;
}

function secondsToMinutes(seconds, fallback) {
  const number = Number(seconds);
  if (!isFinite(number) || number <= 0) {
    return fallback;
  }
  return Math.round((number / 60) * 100) / 100;
}

function minutesToSeconds(minutes) {
  const number = Number(minutes);
  if (!isFinite(number)) {
    return 0;
  }
  return Math.round(number * 60);
}

function validTime(value) {
  return /^([01][0-9]|2[0-3]):[0-5][0-9]$/.test(String(value || ""));
}

function validHttpUrl(value) {
  const text = String(value || "").trim();
  if (!/^https?:\/\//i.test(text)) {
    return false;
  }
  try {
    // eslint-disable-next-line no-new
    new URL(text);
    return true;
  } catch (error) {
    return false;
  }
}

function sourceKey(type, id) {
  return String(type) + ":" + String(id);
}

export default {
  name: "auto-follow-up-wizard",
  components: {
    LayoutHeader,
    LayoutSidebar,
    Multiselect,
    ChatComponent,
    // Vue 2 me-resolve <afu-whatsapp-preview> ke "AfuWhatsappPreview",
    // jadi key registrasi harus sama persis dengan tag kebab.
    "afu-whatsapp-preview": AfuWhatsAppPreview
  },
  data() {
    return {
      flowId: "",
      pageLoading: false,
      loadError: "",
      form: defaultForm(),
      previewStepIndex: 0,
      delayMinMinutes: 1,
      delayMaxMinutes: 10,
      licenseOptions: [],
      licenseCache: {},
      licenseLoading: false,
      licenseError: "",
      licenseRequestId: 0,
      licenseQuota: null,
      licenseQuotaLoading: false,
      licenseQuotaError: "",
      quotaRequestId: 0,
      quotaModalVisible: false,
      quotaReason: "",
      quotaNewLimit: null,
      quotaSaving: false,
      quotaError: "",
      groupOptions: [],
      tagOptions: [],
      contactOptions: [],
      groupLoading: false,
      tagLoading: false,
      contactLoading: false,
      groupError: "",
      tagError: "",
      contactError: "",
      sourceMode: "group",
      selectedGroups: [],
      selectedTags: [],
      selectedContacts: [],
      originalSources: [],
      deletedStepIds: [],
      errors: {},
      stepErrors: {},
      serverErrors: {},
      serverError: "",
      validationSummary: "",
      activationError: "",
      isSaving: false,
      isActivating: false,
      saveIntent: "",
      initialSnapshot: "",
      allowLeave: false
    };
  },
  computed: {
    isEdit() {
      return !!this.flowId;
    },
    pageTitle() {
      return this.isEdit ? "Ubah Alur Follow Up" : "Buat Alur Follow Up";
    },
    autoSwitchEnabled() {
      const session = this.$store.state.login.memberSession || {};
      return session.auto_switching_status == 1;
    },
    selectedLicenseId: {
      get() {
        return this.form.license_id || undefined;
      },
      set(value) {
        this.form.license_id = value || "";
      }
    },
    isStructureLocked() {
      return (
        this.isEdit &&
        ["active", "paused"].indexOf(this.form.status) !== -1
      );
    },
    isReadOnly() {
      return (
        this.isEdit &&
        ["completed", "cancelled"].indexOf(this.form.status) !== -1
      );
    },
    isBusy() {
      return this.isSaving || this.isActivating;
    },
    isUploading() {
      return (this.form.steps || []).some(step => step.uploading);
    },
    canSaveDraft() {
      return !this.isReadOnly && this.form.status === "draft";
    },
    canActivate() {
      return !this.isReadOnly && this.form.status === "draft";
    },
    canSaveChanges() {
      return (
        !this.isReadOnly &&
        ["active", "paused"].indexOf(this.form.status) !== -1
      );
    },
    licenseSelectOptions() {
      const options = (this.licenseOptions || []).slice();
      const id = this.form.license_id;
      if (
        id &&
        !options.some(option => String(option.id) === String(id))
      ) {
        const cached = this.licenseCache[String(id)];
        options.unshift(
          cached
            ? Object.assign({}, cached)
            : {
                id: id,
                license_name: id,
                license_phone_number: "",
                status_label: "",
                is_missing: true
              }
        );
      }
      return options;
    },
    selectedLicense() {
      const id = this.form.license_id;
      if (!id) {
        return null;
      }
      return (
        this.licenseSelectOptions.find(
          option => String(option.id) === String(id)
        ) || null
      );
    },
    selectedLicenseWarning() {
      const license = this.selectedLicense;
      if (!license) {
        return "";
      }
      if (license.is_missing) {
        return "Nomor WhatsApp ini tidak ditemukan di akun Anda. Pilih nomor lain yang tersedia.";
      }
      if (!license.status_label) {
        return "";
      }
      if (license.status_label === "Draft") {
        return "Nomor WhatsApp ini belum aktif. Pilih nomor lain atau aktifkan dulu sebelum menggunakannya.";
      }
      if (license.status_label === "Expired") {
        return "Masa aktif nomor WhatsApp ini sudah berakhir. Perpanjang dulu sebelum menggunakannya.";
      }
      return "";
    },
    allSelectedSources() {
      const sources = [];
      (this.selectedGroups || []).forEach(group => {
        sources.push({
          source_type: "group",
          source_id: String(group.id),
          source_name: group.name || group.source_name || "Grup tanpa nama",
          contact_count: group.contact_count
        });
      });
      (this.selectedTags || []).forEach(tag => {
        sources.push({
          source_type: "tag",
          source_id: String(tag.id),
          source_name: tag.name || tag.source_name || "Tag tanpa nama",
          contact_count: tag.contact_count
        });
      });
      (this.selectedContacts || []).forEach(contact => {
        sources.push({
          source_type: "manual",
          source_id: String(contact.id),
          source_name:
            contact.first_name ||
            contact.name ||
            contact.source_name ||
            "Kontak",
          contact_count: contact.contact_count
        });
      });
      return sources;
    },
    selectedSourceCount() {
      return this.allSelectedSources.length;
    },
    contactEstimate() {
      let count = (this.selectedContacts || []).length;
      let pending = false;
      let error = false;
      [this.selectedGroups || [], this.selectedTags || []].forEach(list => {
        list.forEach(source => {
          if (source._countError) {
            error = true;
          } else if (
            typeof source.contact_count === "undefined" ||
            source.contact_count === null
          ) {
            pending = true;
          } else {
            count += Number(source.contact_count) || 0;
          }
        });
      });
      return { count: count, pending: pending, error: error };
    },
    contactEstimateLabel() {
      if (!this.selectedSourceCount) {
        return "0";
      }
      if (this.contactEstimate.error) {
        return "—";
      }
      if (this.contactEstimate.pending) {
        return "Menghitung...";
      }
      return String(this.contactEstimate.count);
    },
    delayWindowCrossesMidnight() {
      const start = String(this.form.sending_start_time || "");
      const end = String(this.form.sending_end_time || "");
      if (!validTime(start) || !validTime(end)) {
        return false;
      }
      return start > end;
    },
    sendingWindowAlwaysOpen() {
      return (
        !!this.form.sending_start_time &&
        this.form.sending_start_time === this.form.sending_end_time
      );
    },
    uploadAction() {
      return (
        (process.env.VUE_APP_BASE_API || "") +
        APICore() +
        "/omni/only-upload-filev2"
      );
    },
    uploadHeaders() {
      return {
        "X-Auth-Token": "Bearer " + getXAuthToken()
      };
    }
  },
  watch: {
    "$route.query.id": function (value) {
      const id = normalizeQueryId(value);
      if (id === this.flowId) {
        return;
      }
      this.initialize(id);
    },
    "form.steps.length": function () {
      this.clampPreviewIndex();
    },
    selectedGroups: {
      handler() {
        this.syncSourceCounts("group");
      },
      deep: false
    },
    selectedTags: {
      handler() {
        this.syncSourceCounts("tag");
      },
      deep: false
    }
  },
  created() {
    this.initialize(normalizeQueryId(this.$route.query.id));
    this.loadLicenses("");
  },
  beforeRouteLeave(to, from, next) {
    if (this.allowLeave) {
      next();
      return;
    }
    if (this.isBusy) {
      next(false);
      return;
    }
    if (!this.hasUnsavedChanges()) {
      next();
      return;
    }
    this.$fire({
      title: "Tinggalkan halaman?",
      text: "Perubahan yang belum disimpan akan hilang.",
      type: "warning",
      showCancelButton: true,
      confirmButtonText: "Ya, tinggalkan",
      cancelButtonText: "Batal",
      confirmButtonColor: "#FE9B44"
    }).then(result => {
      if (result && result.value) {
        next();
      } else {
        next(false);
      }
    });
  },
  methods: {
    statusLabel(status) {
      return STATUS_LABELS[status] || "Status tidak dikenal";
    },
    statusTone(status) {
      if (
        [
          "draft",
          "active",
          "paused",
          "completed",
          "cancelled"
        ].indexOf(status) !== -1
      ) {
        return status;
      }
      return "draft";
    },
    sourceTypeLabel(type) {
      return SOURCE_TYPE_LABELS[type] || type || "Sumber";
    },
    messageTypeLabel(type) {
      return MESSAGE_TYPE_LABELS[type] || type || "Teks";
    },
    delayUnitLabel(unit) {
      return DELAY_UNIT_LABELS[unit] || unit || "";
    },
    stepDelayLabel(index) {
      return index === 0
        ? "Jeda dari kontak masuk"
        : "Jeda dari pesan sebelumnya";
    },
    sourceDisplay(source) {
      if (!source) {
        return "-";
      }
      return (
        source.source_name ||
        source.name ||
        source.first_name ||
        "Sumber kontak"
      );
    },
    licenseLabel(license) {
      if (!license) {
        return "Belum dipilih";
      }
      const name =
        license.license_name ||
        license.license_phone_number ||
        "Nomor WhatsApp";
      const phone =
        license.license_phone_number && license.license_phone_number !== name
          ? " — " + license.license_phone_number
          : "";
      return name + phone;
    },
    licenseStatusLabel(license) {
      const status = license && license.status_label;
      if (!status) {
        return "";
      }
      const labels = {
        active: "Aktif",
        draft: "Belum aktif",
        expired: "Kedaluwarsa",
        inactive: "Tidak aktif"
      };
      return labels[String(status).toLowerCase()] || "Status tidak diketahui";
    },
    contactLabel(contact) {
      if (!contact) {
        return "-";
      }
      const name = contact.first_name || contact.name || "Kontak";
      const phone = contact.phone_number
        ? " — " + contact.phone_number
        : "";
      return name + phone;
    },
    fieldError(field) {
      return this.errors[field] || this.serverErrors[field] || "";
    },
    stepFieldError(step, field) {
      if (!step || !step._key) {
        return "";
      }
      const errors = this.stepErrors[step._key] || {};
      return errors[field] || "";
    },
    extractErrorMessage(error, fallback) {
      let message = "";
      if (
        error &&
        error.response &&
        error.response.data &&
        error.response.data.msg
      ) {
        message = error.response.data.msg;
      } else if (error && error.message) {
        message = error.message;
      }
      if (!message) {
        return fallback;
      }
      return String(message)
        .replace(/<br\s*\/?>/gi, " ")
        .replace(/<[^>]*>/g, "")
        .trim();
    },
    friendlyErrorMessage(message, fallback) {
      const text = String(message || "").trim();
      if (!text) {
        return fallback;
      }
      const lower = text.toLowerCase();

      if (lower.indexOf("flow name is required") !== -1) {
        return "Nama alur wajib diisi.";
      }
      if (lower.indexOf("flow name maximum") !== -1) {
        return "Nama alur maksimal 255 karakter.";
      }
      if (lower.indexOf("at least one step") !== -1) {
        return "Tambahkan minimal satu pesan.";
      }
      if (
        lower.indexOf("batch_min_contacts") !== -1 ||
        lower.indexOf("batch_max_contacts") !== -1
      ) {
        return "Jumlah kontak per pengiriman belum sesuai. Periksa batas minimal dan maksimal.";
      }
      if (lower.indexOf("message_delay") !== -1) {
        return "Jeda pengiriman belum sesuai. Periksa jeda minimum dan maksimum.";
      }
      if (
        lower.indexOf("sending_start_time") !== -1 ||
        lower.indexOf("sending_end_time") !== -1
      ) {
        return "Format jam pengiriman belum sesuai. Gunakan format seperti 08:00.";
      }
      if (lower.indexOf("duplicate step_order") !== -1) {
        return "Urutan pesan tidak boleh sama.";
      }
      if (lower.indexOf("message_media") !== -1) {
        return "Media atau tautan media belum sesuai. Periksa kembali.";
      }
      if (lower.indexOf("message_content") !== -1) {
        return "Isi pesan belum lengkap. Periksa kembali.";
      }
      if (lower.indexOf("flow cannot be activated from status") !== -1) {
        return "Alur ini tidak bisa diaktifkan pada status saat ini.";
      }
      if (
        lower.indexOf("sender license") !== -1 ||
        lower.indexOf("license is required") !== -1 ||
        lower.indexOf("license is not active") !== -1 ||
        lower.indexOf("license is not found") !== -1
      ) {
        return "Nomor WhatsApp pengirim tidak ditemukan atau belum aktif. Pilih nomor lain yang tersedia.";
      }
      if (
        lower.indexOf("no contacts found") !== -1 ||
        lower.indexOf("contact source") !== -1
      ) {
        return "Belum ada kontak yang sesuai dengan sumber yang dipilih. Periksa grup, tag, atau kontak.";
      }
      if (lower.indexOf("steps cannot") !== -1) {
        return "Pesan dan sumber kontak tidak dapat diubah saat alur berjalan atau dijeda.";
      }
      if (lower.indexOf("only active flows can be paused") !== -1) {
        return "Alur ini belum bisa dijeda karena statusnya tidak aktif.";
      }
      if (lower.indexOf("only paused flows can be resumed") !== -1) {
        return "Alur ini belum bisa dilanjutkan karena tidak sedang dijeda.";
      }
      if (lower.indexOf("only active or paused flows can be cancelled") !== -1) {
        return "Alur ini tidak dapat dibatalkan pada status saat ini.";
      }
      if (
        lower.indexOf("auto follow up flow is not found") !== -1 ||
        lower.indexOf("flow is not found") !== -1
      ) {
        return "Alur tidak ditemukan. Muat ulang halaman daftar.";
      }
      if (lower.indexOf("data yang dikirim tidak valid") !== -1) {
        return "Data yang dikirim belum sesuai. Periksa kembali pengaturan alur.";
      }
      if (lower.indexOf("terjadi kesalahan pada server") !== -1) {
        return "Terjadi kendala saat memproses permintaan. Coba lagi.";
      }
      if (
        lower.indexOf("unauthorized") !== -1 ||
        lower.indexOf("forbidden") !== -1 ||
        lower.indexOf("not authorized") !== -1 ||
        lower.indexOf("permission") !== -1
      ) {
        return "Anda tidak memiliki akses untuk tindakan ini.";
      }
      if (
        lower.indexOf("timeout") !== -1 ||
        lower.indexOf("network") !== -1 ||
        lower.indexOf("connection") !== -1
      ) {
        return "Koneksi bermasalah. Periksa jaringan Anda dan coba lagi.";
      }
      if (lower.indexOf("not found") !== -1) {
        return "Data yang diminta tidak ditemukan. Muat ulang halaman.";
      }
      if (
        lower.indexOf("sql") !== -1 ||
        lower.indexOf("select ") !== -1 ||
        lower.indexOf("table") !== -1 ||
        lower.indexOf("column") !== -1 ||
        lower.indexOf("backend") !== -1 ||
        lower.indexOf("redis") !== -1 ||
        lower.indexOf("worker") !== -1 ||
        lower.indexOf("invalid") !== -1 ||
        lower.indexOf("failed") !== -1 ||
        lower.indexOf("error") !== -1 ||
        text.indexOf("<") !== -1
      ) {
        return fallback;
      }

      return text;
    },
    mapServerError(message) {
      const text = String(message || "");
      const lower = text.toLowerCase();
      const errors = {};

      if (
        lower.indexOf("flow name is required") !== -1
      ) {
        errors.name = "Nama alur wajib diisi.";
      } else if (lower.indexOf("flow name maximum") !== -1) {
        errors.name = "Nama alur maksimal 255 karakter.";
      }

      if (
        lower.indexOf("sender license") !== -1 ||
        lower.indexOf("license is required") !== -1 ||
        lower.indexOf("license is not active") !== -1
      ) {
        errors.license_id =
          "Nomor WhatsApp pengirim tidak ditemukan atau belum aktif. Pilih nomor lain yang tersedia.";
      }

      if (
        lower.indexOf("contact source") !== -1 ||
        lower.indexOf("contact group") !== -1 ||
        lower.indexOf("contact tag") !== -1 ||
        lower.indexOf("contact is not found") !== -1 ||
        lower.indexOf("no contacts found") !== -1 ||
        lower.indexOf("source_id is required") !== -1 ||
        lower.indexOf("source_type must") !== -1 ||
        lower.indexOf("duplicate source") !== -1
      ) {
        errors.sources =
          "Sumber kontak belum sesuai atau belum memiliki kontak. Periksa grup, tag, atau kontak yang dipilih.";
      }

      if (lower.indexOf("batch_min_contacts") !== -1) {
        errors.batch_min_contacts =
          "Jumlah kontak minimal per pengiriman harus lebih kecil atau sama dengan jumlah maksimal.";
      }
      if (lower.indexOf("batch_min_contacts") !== -1 && lower.indexOf("batch_max_contacts") !== -1) {
        errors.batch_max_contacts =
          "Jumlah kontak maksimal per pengiriman harus lebih besar atau sama dengan jumlah minimal.";
      }
      if (lower.indexOf("message_delay_max_seconds") !== -1) {
        errors.delay_max =
          "Jeda maksimal harus lebih besar atau sama dengan jeda minimal.";
      }
      if (lower.indexOf("sending_start_time") !== -1) {
        errors.sending_start_time = "Gunakan format jam, misalnya 08:00.";
      }
      if (lower.indexOf("sending_end_time") !== -1) {
        errors.sending_end_time = "Gunakan format jam, misalnya 20:00.";
      }

      if (
        lower.indexOf("at least one step") !== -1 ||
        lower.indexOf("duplicate step_order") !== -1 ||
        lower.indexOf("steps cannot") !== -1
      ) {
        errors.steps =
          lower.indexOf("steps cannot") !== -1
            ? "Pesan dan sumber kontak tidak dapat diubah saat alur berjalan atau dijeda."
            : "Periksa kembali pesan yang ditandai.";
      }

      const stepMatch = lower.match(/step\s+(\d+)/);
      if (stepMatch) {
        const order = Number(stepMatch[1]);
        const step = (this.form.steps || []).find(
          item => Number(item.step_order) === order
        );
        if (step) {
          if (!this.stepErrors[step._key]) {
            this.$set(this.stepErrors, step._key, {});
          }
          if (lower.indexOf("message_content") !== -1) {
            this.$set(
              this.stepErrors[step._key],
              "content",
              "Isi pesan wajib diisi."
            );
          }
          if (
            lower.indexOf("message_media") !== -1 ||
            lower.indexOf("message_media is required") !== -1
          ) {
            this.$set(
              this.stepErrors[step._key],
              "media",
              "Pilih media atau masukkan tautan media yang valid."
            );
          }
          if (lower.indexOf("delay_value") !== -1) {
            this.$set(
              this.stepErrors[step._key],
              "delay",
              "Isi jeda dengan angka yang valid."
            );
          }
          if (lower.indexOf("delay_unit") !== -1) {
            this.$set(
              this.stepErrors[step._key],
              "delay",
              "Pilih satuan jeda yang tersedia."
            );
          }
        }
      }

      this.serverErrors = Object.assign({}, this.serverErrors, errors);
    },
    initialize(id) {
      this.flowId = id || "";
      this.allowLeave = false;
      this.pageLoading = true;
      this.loadError = "";
      this.errors = {};
      this.stepErrors = {};
      this.serverErrors = {};
      this.serverError = "";
      this.validationSummary = "";
      this.activationError = "";
      this.deletedStepIds = [];
      this.originalSources = [];
      this.selectedGroups = [];
      this.selectedTags = [];
      this.selectedContacts = [];
      this.sourceMode = "group";
      this.form = defaultForm();
      this.previewStepIndex = 0;
      this.delayMinMinutes = 1;
      this.delayMaxMinutes = 10;
      this.initialSnapshot = this.buildSnapshot();

      if (!id) {
        this.pageLoading = false;
        this.initialSnapshot = this.buildSnapshot();
        return;
      }

      this.$store
        .dispatch("autoFollowUp/detailAutoFollowUp", { id: id })
        .then(response => {
          const detail = response && response.data ? response.data : null;
          if (!detail || !detail.id) {
            throw new Error("Alur tidak ditemukan. Buka kembali dari halaman daftar.");
          }
          this.applyDetail(detail);
          this.initialSnapshot = this.buildSnapshot();
        })
        .catch(error => {
          const rawError = this.extractErrorMessage(
            error,
            "Detail alur belum dapat dimuat. Coba lagi."
          );
          this.loadError = this.friendlyErrorMessage(
            rawError,
            "Detail alur belum dapat dimuat. Coba lagi."
          );
        })
        .finally(() => {
          this.pageLoading = false;
        });
    },
    retryLoad() {
      this.initialize(this.flowId);
    },
    applyDetail(detail) {
      const status = detail.status || "draft";
      if (detail.id) {
        this.flowId = detail.id;
      }
      const stopOnReply =
        typeof detail.stop_on_customer_reply === "undefined"
          ? 1
          : Number(detail.stop_on_customer_reply) === 1
          ? 1
          : 0;

      this.deletedStepIds = [];
      this.form = {
        id: detail.id || "",
        status: status,
        name: detail.name || "",
        description: detail.description || "",
        license_id: detail.license_id ? String(detail.license_id) : "",
        stop_on_customer_reply: stopOnReply,
        daily_contact_limit: positiveNumber(
          detail.daily_contact_limit,
          DEFAULT_DAILY_LIMIT
        ),
        batch_min_contacts: positiveNumber(
          detail.batch_min_contacts,
          DEFAULT_BATCH_MIN
        ),
        batch_max_contacts: positiveNumber(
          detail.batch_max_contacts,
          DEFAULT_BATCH_MAX
        ),
        sending_start_time: detail.sending_start_time || "08:00",
        sending_end_time: detail.sending_end_time || "20:00",
        steps: []
      };

      this.delayMinMinutes = secondsToMinutes(
        detail.message_delay_min_seconds,
        1
      );
      this.delayMaxMinutes = secondsToMinutes(
        detail.message_delay_max_seconds,
        10
      );

      this.loadLicenseQuota();

      const steps = Array.isArray(detail.steps) ? detail.steps : [];
      this.form.steps = steps.map((step, index) => {
        const id = step.id || "";
        return {
          _key: id ? "step-" + id : randomKey("step-load"),
          id: id,
          step_order: Number(step.step_order) || index + 1,
          name: step.name || "",
          delay_value: Number(step.delay_value) || 0,
          delay_unit: step.delay_unit || "hour",
          message_type: step.message_type || "text",
          message_content: step.message_content || "",
          message_media: step.message_media || "",
          is_custom_url:
            Number(step.is_custom_url) === 1 || step.is_custom_url === true,
          media_name: this.fileNameFromUrl(step.message_media || ""),
          uploading: false,
          uploadError: ""
        };
      });

      if (!this.form.steps.length && status === "draft") {
        this.form.steps = [defaultStep(0)];
      }
      if (status === "draft") {
        this.normalizeStepOrders();
      }
      this.previewStepIndex = 0;

      const sources = Array.isArray(detail.sources) ? detail.sources : [];
      this.originalSources = sources.map(source => ({
        source_type: source.source_type,
        source_id: String(source.source_id),
        source_name: source.source_name || ""
      }));
      this.setSelectedSources(sources);
      this.sourceMode = this.firstSourceMode();
    },
    setSelectedSources(sources) {
      this.selectedGroups = [];
      this.selectedTags = [];
      this.selectedContacts = [];

      (sources || []).forEach(source => {
        const id = String(source.source_id || "");
        const name = source.source_name || id;
        if (source.source_type === "group") {
          this.selectedGroups.push({
            id: id,
            name: name,
            contact_count: null,
            _countLoading: false
          });
        } else if (source.source_type === "tag") {
          this.selectedTags.push({
            id: id,
            name: name,
            contact_count: null,
            _countLoading: false
          });
        } else if (source.source_type === "manual") {
          this.selectedContacts.push({
            id: id,
            first_name: name,
            name: name,
            phone_number: "",
            contact_count: null
          });
        }
      });
    },
    firstSourceMode() {
      if (this.selectedGroups.length) {
        return "group";
      }
      if (this.selectedTags.length) {
        return "tag";
      }
      if (this.selectedContacts.length) {
        return "manual";
      }
      return "group";
    },
    normalizeStepOrders() {
      (this.form.steps || []).forEach((step, index) => {
        step.step_order = index + 1;
        if (!step._key) {
          step._key = randomKey("step");
        }
      });
    },
    clampPreviewIndex() {
      const max = Math.max((this.form.steps || []).length - 1, 0);
      if (!isFinite(Number(this.previewStepIndex))) {
        this.previewStepIndex = 0;
        return;
      }
      this.previewStepIndex = Math.min(Math.max(this.previewStepIndex, 0), max);
    },
    selectPreviewStep(index, scrollToPanel) {
      const max = Math.max((this.form.steps || []).length - 1, 0);
      const target = Math.min(Math.max(Number(index) || 0, 0), max);
      this.previewStepIndex = target;
      if (scrollToPanel && typeof window !== "undefined" && window.innerWidth < 1200) {
        this.$nextTick(() => {
          const panel = this.$el.querySelector(".afu-steps-preview-col");
          if (panel && panel.scrollIntoView) {
            panel.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        });
      }
    },
    addStep() {
      if (this.isStructureLocked || this.isReadOnly) {
        return;
      }
      this.form.steps.push(defaultStep(this.form.steps.length));
      this.normalizeStepOrders();
      this.previewStepIndex = this.form.steps.length - 1;
      this.$nextTick(() => {
        const cards = this.$el.querySelectorAll(".afu-step-card");
        const last = cards[cards.length - 1];
        if (last && last.scrollIntoView) {
          last.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
    },
    removeStep(index) {
      if (this.isStructureLocked || this.isReadOnly) {
        return;
      }
      if (this.form.steps.length <= 1) {
        return;
      }
      const step = this.form.steps[index];
      this.$fire({
        title: "Hapus pesan?",
        text:
          "Pesan " +
          (index + 1) +
          (step && step.name ? ' ("' + step.name + '")' : "") +
          " akan dihapus dari alur.",
        type: "warning",
        showCancelButton: true,
        confirmButtonText: "Ya, hapus",
        cancelButtonText: "Batal",
        confirmButtonColor: "#FE9B44"
      }).then(result => {
        if (!result || !result.value) {
          return;
        }
        if (step && step.id && this.deletedStepIds.indexOf(step.id) === -1) {
          this.deletedStepIds.push(step.id);
        }
        if (step && step._key) {
          this.$delete(this.stepErrors, step._key);
        }
        this.form.steps.splice(index, 1);
        this.normalizeStepOrders();
        if (this.previewStepIndex > index) {
          this.previewStepIndex -= 1;
        }
        this.clampPreviewIndex();
      });
    },
    handleStepTypeChange(step) {
      step.message_media = "";
      step.media_name = "";
      step.uploadError = "";
      step.is_custom_url = false;
      if (step._key) {
        this.$delete(this.stepErrors, step._key);
      }
    },
    onStopOnReplyChange(checked) {
      this.form.stop_on_customer_reply = checked ? 1 : 0;
    },
    onCustomUrlToggle(step, checked) {
      step.is_custom_url = !!checked;
      step.message_media = "";
      step.media_name = "";
      step.uploadError = "";
    },
    clearStepMedia(step) {
      step.message_media = "";
      step.media_name = "";
      step.uploadError = "";
    },
    mediaTypeLabel(type) {
      return MESSAGE_TYPE_LABELS[type] || "Media";
    },
    mediaAccept(step) {
      if (step.message_type === "image") {
        return ".jpg,.jpeg,.png,.gif,.webp,.jfif,image/*";
      }
      if (step.message_type === "video") {
        return ".mp4,.m4v,video/mp4,video/x-m4v";
      }
      if (step.message_type === "document") {
        return ".pdf,.doc,.docx,.xls,.xlsx,.zip,application/pdf";
      }
      return "";
    },
    mediaHint(type) {
      if (type === "image") {
        return "Format gambar: JPG, JPEG, PNG, GIF, WEBP, JFIF. Maksimal 10 MB.";
      }
      if (type === "video") {
        return "Format video: MP4 atau M4V. Maksimal 10 MB.";
      }
      return "Format dokumen: PDF, DOC/DOCX, XLS/XLSX, atau ZIP. Maksimal 10 MB.";
    },
    beforeMediaUpload(step, file) {
      if (this.isStructureLocked || this.isReadOnly) {
        return false;
      }
      step.uploadError = "";
      const size = Number(file && file.size) || 0;
      if (size > 10 * 1024 * 1024) {
        step.uploadError = "Ukuran file maksimal 10 MB.";
        return false;
      }
      const name = String((file && file.name) || "").toLowerCase();
      const patterns = {
        image: /\.(jpe?g|png|gif|webp|jfif)$/i,
        video: /\.(mp4|m4v)$/i,
        document: /\.(pdf|doc|docx|xls|xlsx|zip)$/i
      };
      const pattern = patterns[step.message_type];
      if (!pattern || !pattern.test(name)) {
        step.uploadError =
          "Tipe file tidak sesuai dengan jenis pesan yang dipilih.";
        return false;
      }
      return true;
    },
    handleMediaUpload(step, info) {
      const file = info && info.file ? info.file : null;
      if (!file) {
        return;
      }
      if (file.status === "uploading") {
        step.uploading = true;
        step.uploadError = "";
        return;
      }
      if (file.status === "done") {
        step.uploading = false;
        const response = file.response || {};
        if (
          response &&
          typeof response.status !== "undefined" &&
          Number(response.status) !== 200
        ) {
          step.uploadError = this.friendlyErrorMessage(
            this.extractErrorMessage(
              { message: response.msg },
              "Media belum berhasil diunggah. Coba lagi."
            ),
            "Media belum berhasil diunggah. Coba lagi."
          );
          return;
        }
        const payload =
          response && response.data ? response.data : response;
        let url = "";
        if (typeof payload === "string") {
          url = payload;
        } else if (payload) {
          url = payload.url || payload.file_url || "";
        }
        if (!url) {
          step.uploadError = "Media belum berhasil diunggah. Coba unggah ulang.";
          return;
        }
        step.message_media = url;
        step.media_name = file.name || this.fileNameFromUrl(url);
        step.is_custom_url = false;
        step.uploadError = "";
        if (step._key) {
          const errors = this.stepErrors[step._key];
          if (errors) {
            this.$delete(errors, "media");
          }
        }
        return;
      }
      if (file.status === "error") {
        step.uploading = false;
        const response = file.response || {};
        step.uploadError = this.friendlyErrorMessage(
          this.extractErrorMessage(
            { message: response.msg },
            "Media belum berhasil diunggah. Coba lagi."
          ),
          "Media belum berhasil diunggah. Coba lagi."
        );
      }
    },
    fileNameFromUrl(url) {
      const text = String(url || "");
      if (!text) {
        return "";
      }
      const clean = text.split("?")[0].split("#")[0];
      const parts = clean.split("/");
      const name = parts[parts.length - 1];
      try {
        return decodeURIComponent(name);
      } catch (error) {
        return name;
      }
    },
    onLicenseChange() {
      this.licenseQuota = null;
      this.licenseQuotaError = "";
      this.loadLicenseQuota();
    },
    loadLicenseQuota() {
      const licenseId = this.form.license_id;
      this.quotaRequestId += 1;
      const requestId = this.quotaRequestId;
      if (!licenseId) {
        this.licenseQuota = null;
        this.licenseQuotaLoading = false;
        this.licenseQuotaError = "";
        return;
      }
      this.licenseQuotaLoading = true;
      this.licenseQuotaError = "";
      this.$store
        .dispatch("autoFollowUp/licenseQuotaAutoFollowUp", {
          license_id: licenseId
        })
        .then(response => {
          if (requestId !== this.quotaRequestId) {
            return;
          }
          const info = response && response.data ? response.data : null;
          if (!info) {
            throw new Error("Informasi kuota lisensi tidak tersedia.");
          }
          this.licenseQuota = info;
          this.form.daily_contact_limit = Number(info.daily_limit) || DEFAULT_DAILY_LIMIT;
        })
        .catch(error => {
          if (requestId !== this.quotaRequestId) {
            return;
          }
          this.licenseQuotaError = this.extractErrorMessage(
            error,
            "Batas kuota lisensi belum dapat dimuat."
          );
        })
        .finally(() => {
          if (requestId === this.quotaRequestId) {
            this.licenseQuotaLoading = false;
          }
        });
    },
    openQuotaSettings() {
      if (!this.licenseQuota) {
        return;
      }
      this.quotaNewLimit = Number(this.licenseQuota.daily_limit) || null;
      this.quotaReason = "";
      this.quotaError = "";
      this.quotaModalVisible = true;
    },
    closeQuotaSettings() {
      if (this.quotaSaving) {
        return;
      }
      this.quotaModalVisible = false;
      this.quotaError = "";
    },
    saveQuotaSettings() {
      if (this.quotaSaving) {
        return;
      }
      const reason = (this.quotaReason || "").trim();
      if (!reason) {
        this.quotaError = "Alasan perubahan batas lisensi wajib diisi.";
        return;
      }
      const limit = Number(this.quotaNewLimit);
      if (!isFinite(limit) || limit < 1) {
        this.quotaError = "Batas kontak baru harus berupa angka lebih dari 0.";
        return;
      }
      this.quotaSaving = true;
      this.quotaError = "";
      this.$store
        .dispatch("autoFollowUp/resetLicenseQuotaAutoFollowUp", {
          license_id: this.form.license_id,
          reason,
          daily_limit: limit
        })
        .then(response => {
          const info = response && response.data ? response.data : null;
          if (info) {
            this.licenseQuota = info;
            this.form.daily_contact_limit = Number(info.daily_limit) || limit;
          }
          this.quotaModalVisible = false;
          return this.$fire({
            title: "Batas lisensi diperbarui",
            text:
              "Batas baru berlaku untuk semua Auto Follow Up yang memakai nomor pengirim ini.",
            type: "success",
            timer: 2400
          });
        })
        .catch(error => {
          this.quotaError = this.extractErrorMessage(
            error,
            "Batas kuota lisensi belum dapat diperbarui."
          );
        })
        .finally(() => {
          this.quotaSaving = false;
        });
    },
    searchLicenses(query) {
      this.loadLicenses(query || "");
    },
    loadLicenses(query) {
      this.licenseRequestId += 1;
      const requestId = this.licenseRequestId;
      this.licenseLoading = true;
      this.licenseError = "";
      this.$store
        .dispatch("license/getLicenseList", {
          license_name: query || "",
          id: "",
          license_status: "",
          page: 0,
          perPage: 50
        })
        .then(response => {
          if (requestId !== this.licenseRequestId) {
            return;
          }
          const rows =
            response &&
            response.data &&
            Array.isArray(response.data.data)
              ? response.data.data
              : [];
          const normalizedRows = rows.map(license =>
            Object.assign({}, license, { id: String(license.id) })
          );
          const cache = Object.assign({}, this.licenseCache);
          normalizedRows.forEach(license => {
            cache[String(license.id)] = license;
          });
          this.licenseCache = cache;
          this.licenseOptions = normalizedRows;
        })
        .catch(() => {
          if (requestId !== this.licenseRequestId) {
            return;
          }
          this.licenseError = "Daftar nomor WhatsApp belum dapat dimuat. Coba lagi.";
        })
        .finally(() => {
          if (requestId === this.licenseRequestId) {
            this.licenseLoading = false;
          }
        });
    },
    loadGroups(query) {
      this.groupLoading = true;
      this.groupError = "";
      this.$store
        .dispatch("contact/getGroupList", {
          name: query || "",
          page: 0,
          perPage: 20,
          rendered: 0,
          totalRecords: 0
        })
        .then(response => {
          const rows =
            response && Array.isArray(response.data) ? response.data : [];
          this.groupOptions = rows.map(group => ({
            ...group,
            id: String(group.id),
            name: group.name || "Grup tanpa nama"
          }));
          this.mergeSelectedGroupDetails();
        })
        .catch(() => {
          this.groupError = "Grup kontak belum dapat dimuat. Coba lagi.";
        })
        .finally(() => {
          this.groupLoading = false;
        });
    },
    loadTags(query) {
      this.tagLoading = true;
      this.tagError = "";
      this.$store
        .dispatch("crm/listTagsBC", {
          name: query || "",
          page: 0,
          perPage: 20,
          rendered: 0,
          totalRecords: 0
        })
        .then(response => {
          const rows =
            response &&
            response.data &&
            Array.isArray(response.data.data)
              ? response.data.data
              : [];
          this.tagOptions = rows.map(tag => ({
            ...tag,
            id: String(tag.id),
            name: tag.name || "Tag tanpa nama"
          }));
          this.mergeSelectedTagDetails();
        })
        .catch(() => {
          this.tagError = "Tag belum dapat dimuat. Coba lagi.";
        })
        .finally(() => {
          this.tagLoading = false;
        });
    },
    loadContacts(query) {
      this.contactLoading = true;
      this.contactError = "";
      this.$store
        .dispatch("contact/getContactList", {
          keywords: query || "",
          page: 0,
          perPage: 20,
          rendered: 0,
          totalRecords: 0
        })
        .then(response => {
          const rows =
            response && Array.isArray(response.data) ? response.data : [];
          this.contactOptions = rows
            .filter(contact => contact.is_group != 1)
            .map(contact => ({
              ...contact,
              id: String(contact.id),
              is_group: 0
            }));
          this.mergeSelectedContactDetails();
        })
        .catch(() => {
          this.contactError = "Kontak belum dapat dimuat. Coba lagi.";
        })
        .finally(() => {
          this.contactLoading = false;
        });
    },
    mergeSelectedGroupDetails() {
      this.selectedGroups.forEach(selected => {
        const match = this.groupOptions.find(
          option => String(option.id) === String(selected.id)
        );
        if (match) {
          this.$set(selected, "name", match.name);
        }
      });
    },
    mergeSelectedTagDetails() {
      this.selectedTags.forEach(selected => {
        const match = this.tagOptions.find(
          option => String(option.id) === String(selected.id)
        );
        if (match) {
          this.$set(selected, "name", match.name);
        }
      });
    },
    mergeSelectedContactDetails() {
      this.selectedContacts.forEach(selected => {
        const match = this.contactOptions.find(
          option => String(option.id) === String(selected.id)
        );
        if (match) {
          this.$set(
            selected,
            "first_name",
            match.first_name || match.name || selected.first_name
          );
          this.$set(selected, "name", match.name || selected.name);
          this.$set(
            selected,
            "phone_number",
            match.phone_number || selected.phone_number || ""
          );
        }
      });
    },
    syncSourceCounts(type) {
      const list =
        type === "group" ? this.selectedGroups : this.selectedTags;
      const action =
        type === "group"
          ? "contact/countContactByGroupId"
          : "crm/countContactByTagId";

      (list || []).forEach(source => {
        if (
          typeof source.contact_count !== "undefined" &&
          source.contact_count !== null
        ) {
          return;
        }
        if (source._countLoading || source._countError) {
          return;
        }
        this.$set(source, "_countLoading", true);
        this.$store
          .dispatch(action, source.id)
          .then(response => {
            const count =
              response && typeof response.data !== "undefined"
                ? Number(response.data)
                : 0;
            this.$set(
              source,
              "contact_count",
              isFinite(count) ? count : 0
            );
            this.$set(source, "_countLoading", false);
          })
          .catch(() => {
            this.$set(source, "_countLoading", false);
            this.$set(source, "_countError", true);
          });
      });
    },
    validateForm() {
      const errors = {};
      const stepErrors = {};

      const name = String(this.form.name || "").trim();
      if (!name) {
        errors.name = "Nama alur wajib diisi.";
      } else if (name.length > 255) {
        errors.name = "Nama alur maksimal 255 karakter.";
      }

      if (!this.form.license_id) {
        errors.license_id = "Pilih nomor WhatsApp pengirim.";
      } else if (
        this.selectedLicense &&
        this.selectedLicense.status_label === "Draft"
      ) {
        errors.license_id = "Nomor WhatsApp pengirim belum aktif.";
      }

      const sources = this.allSelectedSources;
      if (!sources.length) {
        errors.sources = "Pilih minimal satu sumber kontak.";
      } else if (
        sources.some(source => !source.source_id)
      ) {
        errors.sources = "Ada sumber kontak yang belum lengkap.";
      }

      if (!this.form.steps.length) {
        errors.steps = "Tambahkan minimal satu pesan.";
      } else {
        const seenOrders = {};
        this.form.steps.forEach(step => {
          const stepError = {};
          const order = Number(step.step_order);
          if (order > 0 && seenOrders[order]) {
            stepError.content = "Urutan pesan tidak boleh sama.";
          }
          if (order > 0) {
            seenOrders[order] = true;
          }

          if (
            !isFinite(Number(step.delay_value)) ||
            Number(step.delay_value) < 0
          ) {
            stepError.delay = "Jeda pesan harus 0 atau lebih.";
          }
          if (DELAY_UNITS.indexOf(step.delay_unit) === -1) {
            stepError.delay = "Pilih satuan jeda yang tersedia.";
          }
          if (MESSAGE_TYPES.indexOf(step.message_type) === -1) {
            stepError.content = "Pilih jenis pesan yang tersedia.";
          }

          if (step.message_type === "text") {
            if (!String(step.message_content || "").trim()) {
              stepError.content = "Isi pesan wajib diisi.";
            }
          } else {
            if (step.uploading) {
              stepError.media = "Tunggu unggahan media selesai.";
            } else if (!String(step.message_media || "").trim()) {
              stepError.media = "Pilih media atau isi tautan media.";
            } else if (
              step.is_custom_url &&
              !validHttpUrl(step.message_media)
            ) {
              stepError.media = "Tautan media harus diawali http:// atau https://.";
            }
          }

          if (Object.keys(stepError).length) {
            stepErrors[step._key] = stepError;
          }
        });
        if (Object.keys(stepErrors).length) {
          errors.steps = "Periksa kembali pesan yang ditandai.";
        }
      }

      const batchMin = Number(this.form.batch_min_contacts);
      const batchMax = Number(this.form.batch_max_contacts);
      if (!isFinite(batchMin) || batchMin <= 0) {
        errors.batch_min_contacts = "Jumlah kontak minimal per pengiriman wajib diisi.";
      }
      if (!isFinite(batchMax) || batchMax <= 0) {
        errors.batch_max_contacts = "Jumlah kontak maksimal per pengiriman wajib diisi.";
      }
      if (
        isFinite(batchMin) &&
        isFinite(batchMax) &&
        batchMin > batchMax
      ) {
        errors.batch_max_contacts =
          "Jumlah kontak maksimal per pengiriman harus lebih besar atau sama dengan jumlah minimal.";
      }

      const delayMin = Number(this.delayMinMinutes);
      const delayMax = Number(this.delayMaxMinutes);
      if (!isFinite(delayMin) || delayMin <= 0) {
        errors.delay_min = "Jeda minimal wajib diisi.";
      }
      if (!isFinite(delayMax) || delayMax <= 0) {
        errors.delay_max = "Jeda maksimal wajib diisi.";
      }
      if (
        isFinite(delayMin) &&
        isFinite(delayMax) &&
        delayMin > delayMax
      ) {
        errors.delay_max =
          "Jeda maksimal harus lebih besar atau sama dengan jeda minimal.";
      }

      if (!validTime(this.form.sending_start_time)) {
        errors.sending_start_time = "Gunakan format jam, misalnya 08:00.";
      }
      if (!validTime(this.form.sending_end_time)) {
        errors.sending_end_time = "Gunakan format jam, misalnya 20:00.";
      }

      this.errors = errors;
      this.stepErrors = stepErrors;
      this.validationSummary = Object.keys(errors).length
        ? "Lengkapi kolom yang ditandai sebelum menyimpan."
        : "";

      return Object.keys(errors).length === 0;
    },
    scrollToFirstError() {
      this.$nextTick(() => {
        const element = this.$el.querySelector(
          ".is-invalid, .afu-field-error, .afu-step-error"
        );
        if (element && element.scrollIntoView) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
    },
    buildPayload() {
      const exactSteps = this.isStructureLocked || this.isReadOnly;
      const steps = [];

      if (!exactSteps) {
        this.deletedStepIds.forEach(id => {
          if (id) {
            steps.push({ id: id, deleted: true });
          }
        });
        this.form.steps.forEach((step, index) => {
          steps.push({
            id: step.id || "",
            step_order: index + 1,
            name: String(step.name || "").trim(),
            delay_value: Number(step.delay_value) || 0,
            delay_unit: step.delay_unit || "hour",
            message_type: step.message_type || "text",
            message_content: step.message_content || "",
            message_media: step.message_media || "",
            is_custom_url: !!step.is_custom_url
          });
        });
      } else {
        this.form.steps.forEach((step, index) => {
          steps.push({
            id: step.id || "",
            step_order: Number(step.step_order) || index + 1,
            name: step.name || "",
            delay_value: Number(step.delay_value) || 0,
            delay_unit: step.delay_unit || "hour",
            message_type: step.message_type || "text",
            message_content: step.message_content || "",
            message_media: step.message_media || "",
            is_custom_url: !!step.is_custom_url
          });
        });
      }

      const sources = [];
      if (!exactSteps) {
        const currentKeys = {};
        this.allSelectedSources.forEach(source => {
          currentKeys[sourceKey(source.source_type, source.source_id)] = true;
        });
        this.originalSources.forEach(source => {
          const key = sourceKey(source.source_type, source.source_id);
          if (!currentKeys[key]) {
            sources.push({
              source_type: source.source_type,
              source_id: source.source_id,
              deleted: true
            });
          }
        });
        this.allSelectedSources.forEach(source => {
          sources.push({
            source_type: source.source_type,
            source_id: source.source_id
          });
        });
      } else {
        this.originalSources.forEach(source => {
          sources.push({
            source_type: source.source_type,
            source_id: source.source_id
          });
        });
      }

      const payload = {
        name: String(this.form.name || "").trim(),
        description: this.form.description || "",
        license_id: this.form.license_id,
        stop_on_customer_reply:
          Number(this.form.stop_on_customer_reply) === 1 ? 1 : 0,
        daily_contact_limit: this.licenseQuota
          ? Number(this.licenseQuota.daily_limit) || 0
          : Number(this.form.daily_contact_limit) || 0,
        batch_min_contacts: Number(this.form.batch_min_contacts),
        batch_max_contacts: Number(this.form.batch_max_contacts),
        message_delay_min_seconds: minutesToSeconds(this.delayMinMinutes),
        message_delay_max_seconds: minutesToSeconds(this.delayMaxMinutes),
        sending_start_time: this.form.sending_start_time,
        sending_end_time: this.form.sending_end_time,
        steps: steps,
        sources: sources
      };

      if (this.form.id) {
        payload.id = this.form.id;
      }

      return payload;
    },
    buildSnapshot() {
      return JSON.stringify({
        id: this.form.id || "",
        name: String(this.form.name || "").trim(),
        description: this.form.description || "",
        license_id: this.form.license_id || "",
        stop_on_customer_reply:
          Number(this.form.stop_on_customer_reply) === 1 ? 1 : 0,
        daily_contact_limit: this.licenseQuota
          ? Number(this.licenseQuota.daily_limit) || 0
          : Number(this.form.daily_contact_limit) || 0,
        batch_min_contacts: Number(this.form.batch_min_contacts) || 0,
        batch_max_contacts: Number(this.form.batch_max_contacts) || 0,
        delay_min_minutes: Number(this.delayMinMinutes) || 0,
        delay_max_minutes: Number(this.delayMaxMinutes) || 0,
        sending_start_time: this.form.sending_start_time || "",
        sending_end_time: this.form.sending_end_time || "",
        steps: (this.form.steps || []).map(step => ({
          id: step.id || "",
          step_order: Number(step.step_order) || 0,
          name: step.name || "",
          delay_value: Number(step.delay_value) || 0,
          delay_unit: step.delay_unit || "",
          message_type: step.message_type || "",
          message_content: step.message_content || "",
          message_media: step.message_media || "",
          is_custom_url: !!step.is_custom_url
        })),
        sources: this.allSelectedSources
          .map(source => sourceKey(source.source_type, source.source_id))
          .sort()
      });
    },
    hasUnsavedChanges() {
      return this.buildSnapshot() !== this.initialSnapshot;
    },
    async reloadDetailForEdit(id) {
      const response = await this.$store.dispatch(
        "autoFollowUp/detailAutoFollowUp",
        { id: id }
      );
      const detail = response && response.data ? response.data : null;
      if (!detail || !detail.id) {
        throw new Error("Detail Auto Follow Up tidak ditemukan.");
      }
      this.applyDetail(detail);
      this.initialSnapshot = this.buildSnapshot();
    },
    syncRouteId(id) {
      this.flowId = id;
      if (normalizeQueryId(this.$route.query.id) === String(id)) {
        return;
      }
      this.$router
        .replace({
          path: "/auto-follow-up/wizard",
          query: { id: String(id) }
        })
        .catch(() => {});
    },
    async save(intent) {
      if (this.isBusy || this.isReadOnly) {
        return;
      }
      if (intent === "activate" && this.form.status !== "draft") {
        return;
      }
      if (this.isUploading) {
        this.serverError = "Tunggu unggahan media selesai.";
        return;
      }

      this.serverError = "";
      this.serverErrors = {};
      this.stepErrors = {};
      this.activationError = "";
      this.validationSummary = "";
      if (!this.validateForm()) {
        this.scrollToFirstError();
        return;
      }

      this.isSaving = true;
      this.saveIntent = intent;

      try {
        const response = await this.$store.dispatch(
          "autoFollowUp/storedAutoFollowUp",
          this.buildPayload()
        );
        const saved =
          response && response.data ? response.data : null;
        if (!saved || !saved.id) {
          throw new Error("Alur belum tersimpan dengan benar. Coba lagi.");
        }
        this.form.id = saved.id;
        this.flowId = saved.id;
        this.form.status = saved.status || this.form.status || "draft";
        this.syncRouteId(saved.id);
        this.initialSnapshot = this.buildSnapshot();
      } catch (error) {
        this.isSaving = false;
        this.saveIntent = "";
        const rawError = this.extractErrorMessage(
          error,
          "Alur belum dapat disimpan. Coba lagi."
        );
        this.mapServerError(rawError);
        this.serverError = this.friendlyErrorMessage(
          rawError,
          "Alur belum dapat disimpan. Periksa kembali pengaturan Anda."
        );
        return;
      }

      if (intent === "activate") {
        this.isActivating = true;

        // Reload detail setelah simpan agar ID tahapan/sumber baru terbaca.
        // Ini penting ketika aktivasi gagal dan pengguna memperbaiki form,
        // supaya save berikutnya meng-upsert, bukan membuat duplikat.
        try {
          await this.reloadDetailForEdit(this.form.id);
        } catch (reloadError) {
          this.isSaving = false;
          this.isActivating = false;
          this.saveIntent = "";
          this.activationError =
            "Draf berhasil disimpan, tetapi detail alur belum dapat dimuat. Buka Ubah dari halaman daftar untuk melanjutkan aktivasi.";
          await this.$fire({
            title: "Draf tersimpan",
            text:
              "Draf berhasil disimpan, tetapi detail alur belum dapat dimuat. Buka Ubah dari halaman daftar untuk melanjutkan.",
            type: "warning",
            timer: 3000
          });
          this.allowLeave = true;
          this.$router.push("/auto-follow-up");
          return;
        }

        try {
          await this.$store.dispatch("autoFollowUp/activateAutoFollowUp", {
            id: this.form.id
          });
        } catch (error) {
          this.isSaving = false;
          this.isActivating = false;
          this.saveIntent = "";
          const rawError = this.extractErrorMessage(
            error,
            "Silakan periksa kembali sumber kontak dan pesan."
          );
          this.mapServerError(rawError);
          this.activationError =
            "Draf berhasil disimpan, tetapi alur belum dapat diaktifkan. " +
            this.friendlyErrorMessage(
              rawError,
              "Periksa kembali sumber kontak dan pesan, lalu coba lagi."
            );
          return;
        }

        this.isSaving = false;
        this.isActivating = false;
        this.saveIntent = "";
        await this.$fire({
          title: "Berhasil",
          text: "Alur Follow Up berhasil diaktifkan.",
          type: "success",
          timer: 2200
        });
        this.allowLeave = true;
        this.$router.push("/auto-follow-up");
        return;
      }

      this.isSaving = false;
      this.saveIntent = "";
      const successText =
        this.form.status === "draft"
          ? "Draf berhasil disimpan."
          : "Perubahan berhasil disimpan. Pesan yang sudah dijadwalkan tidak diubah.";
      await this.$fire({
        title: "Berhasil",
        text: successText,
        type: "success",
        timer: 2200
      });
      this.allowLeave = true;
      this.$router.push("/auto-follow-up");
    },
    goBack() {
      if (this.isBusy) {
        return;
      }
      if (!this.hasUnsavedChanges()) {
        this.allowLeave = true;
        this.$router.push("/auto-follow-up");
        return;
      }
      this.$fire({
        title: "Tinggalkan halaman?",
        text: "Perubahan yang belum disimpan akan hilang.",
        type: "warning",
        showCancelButton: true,
        confirmButtonText: "Ya, tinggalkan",
        cancelButtonText: "Batal",
        confirmButtonColor: "#FE9B44"
      }).then(result => {
        if (result && result.value) {
          this.allowLeave = true;
          this.$router.push("/auto-follow-up");
        }
      });
    },
    goToList() {
      this.allowLeave = true;
      this.$router.push("/auto-follow-up");
    },
    scrollToSection(id) {
      const element = document.getElementById(id);
      if (element && element.scrollIntoView) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }
};
</script>

<style scoped>
.afu-beta-inline {
  display: inline-flex;
  align-items: center;
  margin-left: 7px;
  padding: 2px 7px;
  border: 1px solid #ffd8b5;
  border-radius: 999px;
  background: #fff1e5;
  color: #d97a18;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.35;
  letter-spacing: 0.4px;
  vertical-align: middle;
}

.afu-header-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.afu-wizard-body {
  max-width: 1320px;
  margin: 0 auto;
  padding-bottom: 24px;
}

.afu-form-card {
  border: 1px solid #e9ecef;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  margin-bottom: 18px;
  scroll-margin-top: 92px;
}

.afu-card-header {
  display: flex;
  align-items: center;
  background: #fff;
  border-bottom: 1px solid #f1f3f5;
  border-radius: 12px 12px 0 0;
  padding: 16px 20px;
}

.afu-card-header h5 {
  margin: 0 0 2px;
  font-size: 16px;
  font-weight: 600;
}

.afu-section-number {
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  border-radius: 50%;
  background: #fff1e5;
  color: #fe9b44;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  margin-right: 12px;
}

.afu-section-count {
  margin-left: auto;
  background: #f1f3f5;
  color: #6c757d;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
}

.afu-section-nav {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.afu-section-nav a {
  display: inline-flex;
  align-items: center;
  padding: 7px 12px;
  border-radius: 999px;
  border: 1px solid #e9ecef;
  background: #fff;
  color: #6c757d;
  font-size: 13px;
  font-weight: 500;
}

.afu-section-nav a:hover {
  border-color: #fe9b44;
  color: #d97a18;
  text-decoration: none;
}

.afu-primary-btn {
  background-color: #fe9b44;
  border-color: #fe9b44;
  color: #ffffff;
}

.afu-primary-btn:hover,
.afu-primary-btn:focus {
  background-color: #f08a2e;
  border-color: #f08a2e;
  color: #ffffff;
}

.afu-primary-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.afu-status-badge {
  display: inline-flex;
  align-items: center;
  padding: 5px 11px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.afu-status-badge::before {
  content: "";
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin-right: 6px;
  background: currentColor;
}

.afu-status-draft {
  background: #f1f3f5;
  color: #6c757d;
}

.afu-status-active {
  background: #e7f6e4;
  color: #3f8f34;
}

.afu-status-paused {
  background: #fff1e5;
  color: #d97a18;
}

.afu-status-completed {
  background: #e8f0fe;
  color: #1a73e8;
}

.afu-status-cancelled {
  background: #fdecee;
  color: #dc3545;
}

.afu-status-line {
  padding-top: 7px;
}

.afu-info-box {
  background: #f8f9fa;
  border: 1px solid #eef1f4;
  border-radius: 10px;
  padding: 14px 16px;
  color: #6c757d;
  font-size: 13px;
}

.afu-info-box-title {
  font-weight: 600;
  color: #495057;
  margin-bottom: 8px;
}

.afu-info-box-title i {
  color: #fe9b44;
  margin-right: 4px;
}

.afu-license-option-status {
  display: inline-block;
  margin-left: 8px;
  padding: 1px 6px;
  border-radius: 10px;
  background: #f1f3f5;
  color: #6c757d;
  font-size: 10px;
}

.afu-field-error {
  color: #dc3545;
  font-size: 12px;
  margin-top: 5px;
}

.afu-inline-error {
  color: #dc3545;
  font-size: 12px;
  margin-top: 6px;
}

.afu-inline-warning {
  color: #b8860b;
  font-size: 12px;
  line-height: 1.5;
}

.afu-inline-info {
  color: #6c757d;
  font-size: 12px;
}

.afu-source-tabs {
  margin-bottom: 16px;
}

.afu-source-panel {
  margin-bottom: 14px;
}

.afu-source-panel > label {
  font-size: 13px;
  font-weight: 500;
  color: #495057;
  margin-bottom: 6px;
  display: block;
}

.afu-source-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  background: #f8f9fa;
  border-radius: 10px;
  padding: 12px 14px;
  margin-top: 14px;
}

.afu-source-summary-item {
  display: flex;
  flex-direction: column;
  min-width: 94px;
  padding-right: 16px;
  border-right: 1px solid #e9ecef;
}

.afu-source-summary-item:last-child {
  border-right: 0;
}

.afu-source-summary-wide {
  min-width: 180px;
}

.afu-source-summary-value {
  font-size: 18px;
  font-weight: 700;
  color: #343a40;
  line-height: 1.2;
}

.afu-source-summary-item span:last-child {
  font-size: 12px;
  color: #8c8c8c;
}

.afu-readonly-box {
  background: #f8f9fa;
  border-radius: 10px;
  padding: 14px;
}

.afu-source-badge {
  display: inline-block;
  background: #e8f0fe;
  color: #1a73e8;
  padding: 5px 10px;
  border-radius: 999px;
  font-weight: 500;
  font-size: 12px;
  margin: 0 6px 6px 0;
}

.afu-schedule-note {
  background: #f8f9fa;
  border-left: 3px solid #fe9b44;
  border-radius: 0 8px 8px 0;
  padding: 10px 14px;
  color: #6c757d;
  font-size: 13px;
  line-height: 1.55;
  margin-bottom: 16px;
}

.afu-schedule-note i {
  color: #fe9b44;
  margin-right: 4px;
}

.afu-step-card {
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 14px;
  background: #fff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.afu-step-card.is-preview-active {
  border-color: #fe9b44;
  box-shadow: 0 0 0 3px rgba(254, 155, 68, 0.12);
}

.afu-steps-layout {
  align-items: flex-start;
}

.afu-steps-preview-col {
  position: sticky;
  top: 92px;
}

.afu-step-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 12px;
  margin-bottom: 14px;
  border-bottom: 1px solid #f1f3f5;
}

.afu-step-heading {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.afu-step-number {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  background: #fff1e5;
  color: #d97a18;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 700;
}

.afu-step-actions {
  display: flex;
  align-items: center;
  margin-left: auto;
}

.afu-preview-step-btn.is-active {
  color: #ffffff;
  background: #fe9b44;
  border-color: #fe9b44;
}

.afu-preview-step-btn {
  border-radius: 999px;
  font-weight: 600;
}

.afu-step-readonly {
  background: #fcfcfc;
  border-radius: 8px;
  padding: 12px;
}

.afu-step-readonly-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.afu-step-content {
  white-space: pre-wrap;
  word-break: break-word;
  color: #343a40;
  margin-top: 8px;
}

.afu-delay-row {
  display: flex;
  align-items: center;
}

.afu-delay-unit {
  width: 86px;
  margin-left: 6px;
}

.afu-media-section {
  border-top: 1px dashed #e9ecef;
  padding-top: 14px;
  margin-top: 4px;
}

.afu-media-existing {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  background: #f8f9fa;
  border-radius: 8px;
  padding: 8px 10px;
  margin-top: 10px;
  font-size: 13px;
}

.afu-media-existing a {
  max-width: 70%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.afu-add-step-btn {
  width: 100%;
  border-style: dashed;
  padding: 12px;
  font-weight: 600;
}

.afu-switch-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 14px 16px;
  border: 1px solid #eef1f4;
  border-radius: 10px;
  background: #fcfcfc;
}

.afu-form-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 18px;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 12px;
}

.afu-form-actions-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

@media (max-width: 1199.98px) {
  .afu-steps-preview-col {
    position: static;
    margin-top: 18px;
  }
}

@media (max-width: 767.98px) {
  .afu-header-actions {
    width: 100%;
    justify-content: flex-start;
    margin-top: 12px;
  }

  .afu-form-actions {
    position: static;
    display: block;
  }

  .afu-form-actions-left {
    margin-bottom: 10px;
  }

  .afu-form-actions-left .btn,
  .afu-form-actions-right .btn {
    width: 100%;
  }

  .afu-form-actions-right {
    display: block;
  }

  .afu-form-actions-right .btn {
    margin-bottom: 8px;
  }

  .afu-form-actions-right .btn:last-child {
    margin-bottom: 0;
  }

  .afu-step-header {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .afu-step-actions {
    width: 100%;
    margin-top: 8px;
    margin-left: 0;
  }

  .afu-source-summary-item {
    min-width: 80px;
    border-right: 0;
  }

  .afu-source-summary-wide {
    min-width: 100%;
  }

  .afu-switch-row {
    align-items: flex-start;
  }
}
.afu-license-quota-readonly {
  min-height: 34px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  padding: 6px 0;
}

.afu-quota-detail-row {
  display: flex;
  justify-content: space-between;
  padding: 7px 0;
  border-bottom: 1px solid #f0f1f3;
  font-size: 14px;
}

.afu-quota-detail-row:last-child {
  border-bottom: 0;
}
</style>



// WEBPACK FOOTER //
// src/views/autoFollowUp/wizard.vue